import { TrashIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { ClientId, UserId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { UserSelect } from "components/shared/user-select";
import { MessageBanner } from "components/ui/inline-banner";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { useMessage } from "hooks/use-message";
import React, { useCallback, useState } from "react";

interface ManageCollaboratorsModalProps {
    clientId: ClientId;
    clientName: string;
    onDismiss: () => void;
}

export const ManageCollaboratorsModal: React.FC<
    ManageCollaboratorsModalProps
> = props => {
    const { clientId, clientName, onDismiss } = props;
    const [selectedUserId, setSelectedUserId] = useState<UserId | null | "">("");
    const [isAdding, setIsAdding] = useState(false);
    const [isRemoving, setIsRemoving] = useState<UserId | null>(null);
    const { message, setMessage } = useMessage();

    const trpcClient = trpc.useTRPC();

    const collaboratorsQuery = useQuery(
        trpcClient.collaborators.listForClient.queryOptions({ clientId }),
    );

    const addCollaboratorMutation = useMutation({
        ...trpcClient.collaborators.add.mutationOptions(),
        onSuccess: () => {
            setMessage("success", "Collaborator added successfully");
            setSelectedUserId(null);
            collaboratorsQuery.refetch();
            setIsAdding(false);
        },
        onError: error => {
            setMessage("error", error.message || "Failed to add collaborator");
            setIsAdding(false);
        },
    });

    const removeCollaboratorMutation = useMutation({
        ...trpcClient.collaborators.remove.mutationOptions(),
        onSuccess: () => {
            setMessage("success", "Collaborator removed successfully");
            collaboratorsQuery.refetch();
            setIsRemoving(null);
        },
        onError: error => {
            setMessage("error", error.message || "Failed to remove collaborator");
            setIsRemoving(null);
        },
    });

    const handleAddCollaborator = useCallback(() => {
        if (!selectedUserId || selectedUserId === "") {
            setMessage("error", "Please select a user to add as collaborator");
            return;
        }
        setIsAdding(true);
        addCollaboratorMutation.mutate({
            clientId,
            collaboratorUserId: selectedUserId as UserId,
        });
    }, [selectedUserId, clientId, addCollaboratorMutation, setMessage]);

    const handleRemoveCollaborator = useCallback(
        (collaboratorUserId: UserId) => {
            setIsRemoving(collaboratorUserId);
            removeCollaboratorMutation.mutate({
                clientId,
                collaboratorUserId,
            });
        },
        [clientId, removeCollaboratorMutation],
    );

    const collaborators = collaboratorsQuery.data || [];

    return (
        <Panel
            type={PanelType.large}
            headerText={`Manage Collaborators for ${clientName}`}
            onDismiss={onDismiss}
            isOpen={true}>
            <div className="space-y-6">
                <MessageBanner message={message} />

                <div className="border-t pt-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Add Collaborator
                    </h3>
                    <div className="flex gap-3 items-end">
                        <div className="flex-1">
                            <UserSelect
                                label="Select User"
                                value={selectedUserId}
                                onChange={value =>
                                    setSelectedUserId(value as UserId | null)
                                }
                                includeEveryone={false}
                                includeUnassigned={true}
                                unassignedLabel="Select a user..."
                                disabled={isAdding}
                                required={false}
                            />
                        </div>
                        <PrimaryButton
                            onClick={handleAddCollaborator}
                            disabled={!selectedUserId || isAdding}>
                            {isAdding ? "Adding..." : "Add Collaborator"}
                        </PrimaryButton>
                    </div>
                </div>

                <div className="border-t pt-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Current Collaborators
                    </h3>
                    {collaboratorsQuery.isLoading ? (
                        <div className="text-gray-500">Loading collaborators...</div>
                    ) : collaborators.length === 0 ? (
                        <div className="flex items-center gap-2 text-gray-500">
                            <UserGroupIcon className="h-5 w-5" />
                            <span>No collaborators have been added yet</span>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {collaborators.map(collaborator => (
                                <div
                                    key={collaborator.id}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <UserGroupIcon className="h-5 w-5 text-gray-400" />
                                        <div>
                                            <div className="font-medium text-gray-900">
                                                {collaborator.preferredName}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() =>
                                            handleRemoveCollaborator(
                                                collaborator.userId as UserId,
                                            )
                                        }
                                        disabled={isRemoving === collaborator.userId}
                                        className="p-1 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                                        title="Remove collaborator">
                                        {isRemoving === collaborator.userId ? (
                                            <span className="text-gray-400 text-sm">
                                                Removing...
                                            </span>
                                        ) : (
                                            <TrashIcon className="h-5 w-5 text-gray-500 hover:text-red-600" />
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Panel>
    );
};
